import { useState } from "react";
import { DEFAULT_SETTINGS, PARAM_KEYS, type ScoreSettings } from "../utils/drivingScore";

const LABELS = {
  eco: "Eco velocidad",
  idle: "Ralentí",
  harshBrake: "Frenados bruscos",
  harshAccel: "Aceleraciones bruscas",
  harshTurn: "Giros bruscos",
  speeding: "Excesos",
} as const;

// Mismos rangos que valida el API (modules/companies/drivingScoreSettings.js).
const FIELDS: { key: Exclude<keyof ScoreSettings, "weights">; label: string; unit: string; min: number; max: number; help: string }[] = [
  { key: "ecoSpeedKmh", label: "Eco velocidad", unit: "km/h", min: 40, max: 130, help: "Manejar a esta velocidad o menos cuenta como eco." },
  { key: "speedLimitKmh", label: "Límite de exceso", unit: "km/h", min: 40, max: 150, help: "Arriba de esto es exceso de velocidad." },
  { key: "idleMinutes", label: "Ralentí después de", unit: "min", min: 1, max: 60, help: "Motor encendido y detenido por más de esto." },
  { key: "eventsPer100kmForZero", label: "Bruscos para llegar a 0", unit: "por 100 km", min: 1, max: 100, help: "Con tantos eventos cada 100 km ese parámetro queda en 0." },
];

interface Props {
  initial: ScoreSettings;
  isDarkMode: boolean;
  sessionOnly: boolean;
  onClose: () => void;
  onSave: (s: ScoreSettings) => Promise<void>;
}

export default function BehaviorSettingsModal({ initial, isDarkMode, sessionOnly, onClose, onSave }: Props) {
  const [draft, setDraft] = useState<ScoreSettings>(initial);
  const [saving, setSaving] = useState(false);

  const errors = FIELDS.filter((f) => !(draft[f.key] >= f.min && draft[f.key] <= f.max)).map((f) => `${f.label}: entre ${f.min} y ${f.max}`);
  if (PARAM_KEYS.every((k) => draft.weights[k] === 0)) errors.push("Al menos un parámetro debe contar (peso mayor a 0).");

  const panel = isDarkMode ? "bg-dark-card text-white border-dark-border" : "bg-white text-gray-900 border-gray-200";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const input = isDarkMode ? "bg-white/5 border-dark-border" : "bg-white border-gray-300";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="ajustes-titulo" onClick={onClose}>
      <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border shadow-2xl p-5 ${panel}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 id="ajustes-titulo" className="text-lg font-semibold">Ajustes de la calificación</h2>
            <p className={`text-xs mt-1 ${muted}`}>
              Valen para toda la compañía seleccionada; los cambia el admin reseller o el administrador de la compañía.
              Cambiarlos recalifica al instante lo que ya está en pantalla.
              {sessionOnly && " El servidor todavía no los guarda: se aplican sólo en esta sesión."}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className={`text-xl leading-none ${muted}`}>
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <label key={f.key} className="flex flex-col gap-1 text-sm">
              <span className="font-medium">{f.label}</span>
              <span className="flex items-center gap-2">
                <input
                  type="number"
                  min={f.min}
                  max={f.max}
                  value={Number.isFinite(draft[f.key]) ? draft[f.key] : ""}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value === "" ? NaN : Number(e.target.value) })}
                  className={`w-24 rounded-md border px-2 py-1.5 tabular-nums ${input}`}
                />
                <span className={`text-xs ${muted}`}>{f.unit}</span>
              </span>
              <span className={`text-xs ${muted}`}>{f.help}</span>
            </label>
          ))}
        </div>

        <h3 className="text-sm font-semibold mt-6 mb-1">Peso de cada parámetro</h3>
        <p className={`text-xs mb-3 ${muted}`}>0 = no cuenta en la calificación; 10 = lo que más pesa.</p>
        <div className="flex flex-col gap-3">
          {PARAM_KEYS.map((k) => (
            <label key={k} className="grid grid-cols-[10rem_1fr_2rem] items-center gap-3 text-sm">
              <span>{LABELS[k]}</span>
              <input
                type="range"
                min={0}
                max={10}
                step={1}
                value={draft.weights[k]}
                onChange={(e) => setDraft({ ...draft, weights: { ...draft.weights, [k]: Number(e.target.value) } })}
                className="accent-primary-500"
                aria-label={`Peso de ${LABELS[k]}`}
              />
              <span className="tabular-nums text-right font-medium">{draft.weights[k]}</span>
            </label>
          ))}
        </div>

        {errors.length > 0 && (
          <ul className="mt-4 text-xs text-red-500 list-disc pl-4">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 mt-6">
          <button type="button" onClick={() => setDraft(DEFAULT_SETTINGS)} className={`text-xs underline ${muted}`}>
            Volver a los valores por omisión
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className={`text-sm px-4 py-2 rounded-md border ${input}`}>
              Cancelar
            </button>
            <button
              type="button"
              disabled={errors.length > 0 || saving}
              onClick={async () => {
                setSaving(true);
                try {
                  await onSave(draft);
                } finally {
                  setSaving(false);
                }
              }}
              className="text-sm px-4 py-2 rounded-md bg-primary-500 text-white disabled:opacity-50"
            >
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
